import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { YourGiftCardPreview } from '../components/YourGiftCardPreview';
import { GiftCardStepper } from '../components/GiftCardStepper';
import {
  getGiftCardDraft,
  patchGiftCardDraft,
} from '../../lib/gift-card-draft';
import { sanitizeName } from '../../lib/validators';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function GiftCardRecipientPage() {
  const navigate = useNavigate();
  // Seed every field from the persistent draft so back-navigation
  // restores the customer's entries instead of blanking them.
  const draft = getGiftCardDraft();
  const incomingAmount = draft?.amount ?? 50;

  const [firstName, setFirstName] = useState(draft?.firstName ?? '');
  const [lastName, setLastName] = useState(draft?.lastName ?? '');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [message, setMessage] = useState(draft?.message ?? '');
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});

  const handleContinue = () => {
    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = 'Recipient first name is required.';
    // Last name is required by the purchase API (and the summary page's
    // pre-pay check), so collect it here — otherwise the customer sails
    // through to the summary and the "Pay Securely" button silently
    // refuses to submit with no explanation.
    if (!lastName.trim()) next.lastName = 'Recipient last name is required.';
    if (!email.trim()) next.email = 'Recipient email is required.';
    else if (!EMAIL_RE.test(email.trim()))
      next.email = 'Enter a valid email address.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    patchGiftCardDraft({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      message,
      amount: draft?.amount,
      customAmount: draft?.customAmount,
      sendDate: draft?.sendDate,
    });
    navigate('/gift-card/delivery');
  };

  const currentStep = 1;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 w-full">
        <GiftCardStepper currentStep={currentStep} />

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left column */}
          <div className="flex-1 max-w-[740px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-2">
              Who's it for?
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px] mb-6">
              Tell us little about the lucky recipient
            </p>

            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <FloatingField
                label="Recipient First Name *"
                placeholder="e.g. Sarah"
                value={firstName}
                onChange={(v) => {
                  setFirstName(sanitizeName(v));
                  if (errors.firstName)
                    setErrors((e) => ({ ...e, firstName: undefined }));
                }}
                error={errors.firstName}
              />
              <FloatingField
                label="Recipient Last Name *"
                placeholder="e.g. Williams"
                value={lastName}
                onChange={(v) => {
                  setLastName(sanitizeName(v));
                  if (errors.lastName)
                    setErrors((e) => ({ ...e, lastName: undefined }));
                }}
                error={errors.lastName}
              />
            </div>

            {/* Email */}
            <div className="mb-2">
              <FloatingField
                label="Recipient's Email Address *"
                placeholder="abc@gmail.com"
                type="email"
                value={email}
                onChange={(v) => {
                  setEmail(v);
                  if (errors.email)
                    setErrors((e) => ({ ...e, email: undefined }));
                }}
                error={errors.email}
              />
            </div>
            <p className="font-['Inter',sans-serif] text-[#3a3335] text-[12px] mb-6">
              We'll send that gift card here -  double check the email.
            </p>

            {/* Personal Message */}
            <div className="relative mb-2">
              <label
                htmlFor="message"
                className="absolute left-[20px] top-[12px] font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px]"
              >
                Your Personal Message
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. Lorem Ipsum is simply dummy text of the printing and typesetting industry."
                rows={4}
                className="w-full pt-[34px] pb-[14px] px-[20px] bg-white border border-[#eee] rounded-[8px] outline-none resize-none font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] placeholder:text-[#908e8e] placeholder:font-bold tracking-[-0.1px] focus:border-[#8e2a2f] transition-colors"
              />
            </div>
            <p className="font-['Inter',sans-serif] text-[#3a3335] text-[12px] mb-10">
              This will appear on the gift card email they recieve.
            </p>

            {/* Buttons */}
            <div className="hidden md:flex items-center gap-3.5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-[54px] w-[110px] rounded-[10px] bg-[#3a33350a] hover:bg-[#3a333514] flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
              >
                <span className="material-icons text-[18px] text-[#908e8e]">arrow_back</span>
                <span className="font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#908e8e] tracking-[-0.1px]">
                  Back
                </span>
              </button>
              <PrimaryButton onClick={handleContinue} className="w-full max-w-[420px] h-[54px]">
                Continue
              </PrimaryButton>
            </div>

            {/* Mobile sticky */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:hidden z-50">
              <PrimaryButton onClick={handleContinue} className="w-full h-[54px]">
                Continue
              </PrimaryButton>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full lg:w-[361px] shrink-0">
            <YourGiftCardPreview
              amount={incomingAmount}
              recipientName={[firstName, lastName].filter(Boolean).join(' ')}
            />

            {/* Price breakdown */}
            <div className="mt-6 border border-[#ede8e9] rounded-[10px] p-5 bg-white">
              <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] tracking-[-0.1px] mb-4">
                Price breakdown
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Gift Card Value
                </span>
                <span className="font-['Inter',sans-serif] text-[#8e2a2f] text-[16px] leading-[24px]">
                  £{incomingAmount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="font-['Inter',sans-serif] text-[#908e8e] text-[16px] leading-[24px]">
                  Delivery
                </span>
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Free . Instant Email
                </span>
              </div>
              <div className="h-px bg-[#ede8e9] w-full mb-4" />
              <div className="flex justify-between items-center">
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px]">
                  Total
                </span>
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px]">
                  £{incomingAmount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="material-icons text-[#8e2a2f] text-[20px] leading-[21px]">
                  redeem
                </span>
                <p className="font-['DM_Sans',sans-serif] font-medium text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                  Redeemable against any Cherry Picked service
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

interface FloatingFieldProps {
  label: string;
  placeholder?: string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
  error?: string;
}

function FloatingField({ label, placeholder, value, type = 'text', onChange, error }: FloatingFieldProps) {
  return (
    <div className="relative">
      <label className="absolute left-[20px] top-[10px] font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] pointer-events-none">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-[64px] pt-[26px] pb-[10px] px-[20px] bg-white border rounded-[8px] outline-none font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] placeholder:text-[#908e8e] placeholder:font-bold tracking-[-0.1px] focus:border-[#8e2a2f] transition-colors ${
          error ? 'border-[#fca5a5] ring-1 ring-[#fca5a5]' : 'border-[#eee]'
        }`}
      />
      {error ? (
        <p className="mt-1 text-[12px] text-[#991b1b] font-['DM_Sans',sans-serif]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
